import { Chip, Stack } from '@mui/joy';
import { useMemo, type ReactElement } from 'react';
import * as yup from 'yup';

import { Add, ContentPasteOutlined } from '@mui/icons-material';
import { IPeriodUnit } from '../../../../generated/graphql.tsx';
import type dayjs from 'dayjs';
import { isDayjsAfterEqual, isValidDayjsDate } from '../../../date.utils.ts';
import HeaderBar from '../../../technical/HeaderBar/HeaderBar.tsx';
import { defaultRowSpacing } from '../../../StackSpacing.ts';
import gYupResolver from '../../../technical/form/gYupResolver.ts';
import { useGraphQLApiError } from '../../../technical/form/UseGraphQLApiError.tsx';
import type { FormInputType } from '../../../technical/form/Form.types.ts';
import { useForm, useWatch } from 'react-hook-form';
import GFormProvider from '../../../technical/form/GFormProvider.tsx';
import { GraphQLApiFormErrorMessage } from 'components/technical/form/GraphQLApiErrorMessage.tsx';
import SubmitButton from '../../../technical/form/SubmitButton.tsx';
import FormInput from 'components/technical/form/FormInput.tsx';
import { FormDateInput } from '../../../technical/form/FormDateInput.tsx';
import FormSelect from '../../../technical/form/FormSelect.tsx';
import { periodUnits } from '../../../bookkeeping/investments/CreateInvestment.validation.ts';
import { DateTimeFormat, formatDate } from 'components/formatter.utils.ts';
import isNil from 'lodash/fp/isNil';
import type { Dayjs } from 'dayjs';

export interface FormOutputRebalancingRule {
  start: dayjs.Dayjs;
  periodUnit: IPeriodUnit;
  periodValue: number;
  tradingFee: number;
}

export type FormOutputFields = {
  name: string;
  rebalancingRule: FormOutputRebalancingRule;
};

export type FormInputFields = FormInputType<FormOutputFields>;

export const defaultName = 'Rule 1';

const RebalancingRuleForm = ({
  defaultValues,
  since,
  onSubmit,
}: {
  defaultValues?: FormInputFields;
  since?: Dayjs;
  onSubmit: ({
    input,
    onErrorAndThrow,
  }: {
    input: FormOutputFields;
    onErrorAndThrow: (error: unknown) => void;
  }) => Promise<void>;
}): ReactElement => {
  const formSchema = useMemo(() => {
    let startDaySchema = yup.mixed().required().test('validDate', 'Date is invalid', isValidDayjsDate);

    if (!isNil(since)) {
      startDaySchema = startDaySchema.test(
        'minDate',
        `Min date is ${formatDate(since, DateTimeFormat.Date)}`,
        (value) => isDayjsAfterEqual(value, since)
      );
    }

    return yup.object({
      name: yup.string().required(),
      rebalancingRule: yup
        .object({
          start: startDaySchema,
          periodUnit: yup.string().required().oneOf(Object.values(IPeriodUnit)),
          periodValue: yup.number().required().min(1),
          tradingFee: yup
            .number()
            .required()
            .min(0, 'Trading fee must be non-negative')
            .max(100, 'Trading fee must be less than 100%'),
        })
        .required(),
    });
  }, [since]);

  const methods = useForm<FormInputFields>({
    resolver: gYupResolver(formSchema),
    defaultValues: {
      name: defaultName,
      ...defaultValues,
      rebalancingRule: {
        tradingFee: '0',
        ...defaultValues?.rebalancingRule,
      },
    },
  });

  const { onErrorAndThrow } = useGraphQLApiError(methods);

  const name = useWatch({
    name: 'name',
    control: methods.control,
  });

  const handleFormSubmit = async (input: FormInputFields): Promise<void> => {
    const data = input as unknown as FormOutputFields;
    await onSubmit({
      input: data,
      onErrorAndThrow,
    });
  };

  const width = 'normal';
  return (
    <Stack spacing={defaultRowSpacing} alignItems="flex-start">
      <HeaderBar
        title={
          <>
            {name ? name : defaultName} <Chip startDecorator={<ContentPasteOutlined fontSize="small" />}>Draft</Chip>
          </>
        }
      />
      <GFormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
          <Stack spacing={defaultRowSpacing}>
            <FormInput<FormInputFields> type="text" name="name" label="Name" width={width} autoComplete="off" />
            <Stack direction="row" spacing={defaultRowSpacing}>
              <FormDateInput<FormInputFields>
                name="rebalancingRule.start"
                label="Start date"
                width={width}
                minDate={since}
              />
              <FormInput<FormInputFields>
                name={'rebalancingRule.periodValue' as const}
                type="number"
                label="Period interval"
                width={width}
              />
              <FormSelect<FormInputFields>
                name={'rebalancingRule.periodUnit' as const}
                options={periodUnits}
                label="Period"
                width={width}
              />
            </Stack>
            <FormInput<FormInputFields>
              name={'rebalancingRule.tradingFee' as const}
              type="number"
              label="Trading fee"
              width={width}
              endDecorator="%"
            />
            <Stack alignItems="flex-start" spacing={defaultRowSpacing}>
              <GraphQLApiFormErrorMessage />
              <SubmitButton width="normal" startDecorator={<Add />}>
                Add rule
              </SubmitButton>
            </Stack>
          </Stack>
        </form>
      </GFormProvider>
    </Stack>
  );
};

export default RebalancingRuleForm;
